import { useEffect, useMemo, useState } from 'react';
import { CreditCard, Printer, Receipt, Wallet } from 'lucide-react';
import { Button, Modal } from '../../../admin-ui';
import { api } from '../../../services/api';
import { calculateInvoiceTotals, formatMoney, prepStationOf } from '../../../lib/billing';

const paymentMethods = [
  { key: 'cash', label: 'Efectivo', icon: Wallet },
  { key: 'card', label: 'Tarjeta', icon: CreditCard },
  { key: 'bizum', label: 'Bizum', icon: Receipt },
  { key: 'transfer', label: 'Transferencia', icon: CreditCard },
  { key: 'other', label: 'Otro', icon: Wallet }
];

function defaultSelection(items) {
  return items.reduce((acc, item) => {
    acc[`${item.orderId}:${item.itemIndex}`] = item.remainingQty;
    return acc;
  }, {});
}

function allocateByAmount(items, amount) {
  let remaining = Math.max(0, Number(amount || 0));
  const selected = [];
  for (const item of items || []) {
    if (remaining <= 0) break;
    const price = Number(item.priceWithTax || 0);
    const maxQty = Number(item.remainingQty || 0);
    if (price <= 0 || maxQty <= 0) continue;
    const lineTotal = Math.round(price * maxQty * 100) / 100;
    if (remaining >= lineTotal - 0.005) {
      selected.push({ ...item, quantity: maxQty });
      remaining = Math.round((remaining - lineTotal) * 100) / 100;
    } else {
      const quantity = Math.min(maxQty, Math.round((remaining / price) * 10000) / 10000);
      if (quantity > 0) selected.push({ ...item, quantity });
      remaining = 0;
    }
  }
  return selected;
}

export default function InvoiceModal({ session, table, isOpen, onClose, onIssued }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [splitMode, setSplitMode] = useState('items');
  const [splitAmount, setSplitAmount] = useState('');
  const [series, setSeries] = useState('T');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [customer, setCustomer] = useState({ name: '', nif: '', address: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState('');
  const sessionId = session?._id;

  useEffect(() => {
    if (!isOpen || !sessionId) return;
    setLoading(true);
    setError('');
    api.get(`/invoices/unpaid?sessionId=${sessionId}`)
      .then((data) => {
        const next = data.items || [];
        setItems(next);
        setSelected(defaultSelection(next));
        setSplitMode('items');
        setSplitAmount('');
      })
      .catch((err) => setError(err.message || 'No se pudieron cargar los items pendientes'))
      .finally(() => setLoading(false));
  }, [isOpen, sessionId]);

  const selectedLines = useMemo(() => items
    .map((item) => ({ ...item, quantity: Number(selected[`${item.orderId}:${item.itemIndex}`] || 0) }))
    .filter((item) => item.quantity > 0), [items, selected]);

  const pendingTotals = useMemo(
    () => calculateInvoiceTotals(items.map((item) => ({ ...item, quantity: item.remainingQty }))),
    [items]
  );
  const amountNumber = Number(String(splitAmount || '').replace(',', '.'));
  const amountLines = useMemo(
    () => allocateByAmount(items, Math.min(Math.max(0, amountNumber || 0), pendingTotals.total || 0)),
    [items, amountNumber, pendingTotals.total]
  );
  const activeLines = splitMode === 'amount' ? amountLines : selectedLines;
  const totals = useMemo(() => calculateInvoiceTotals(activeLines), [activeLines]);

  const setQty = (item, quantity) => {
    const key = `${item.orderId}:${item.itemIndex}`;
    const nextQty = Math.max(0, Math.min(Number(item.remainingQty || 0), Number(quantity || 0)));
    setSelected((prev) => ({ ...prev, [key]: nextQty }));
  };

  const issue = async (print = false) => {
    setError('');
    if (splitMode === 'amount' && (!amountNumber || amountNumber <= 0)) {
      setError('Indica un importe a cobrar mayor que 0.');
      return;
    }
    if (splitMode === 'amount' && amountNumber > pendingTotals.total) {
      setError(`El importe no puede superar el pendiente: ${formatMoney(pendingTotals.total)}.`);
      return;
    }
    if (splitMode === 'items' && !selectedLines.length) {
      setError('Selecciona al menos un item pendiente.');
      return;
    }
    if (series === 'F' && (!customer.name || !customer.nif)) {
      setError('Nombre y NIF son obligatorios para factura completa.');
      return;
    }
    setIssuing(true);
    try {
      const invoice = await api.post('/invoices', {
        sessionId,
        series,
        customer,
        paymentMethod,
        ...(splitMode === 'amount'
          ? { splitAmount: amountNumber }
          : { items: selectedLines.map((item) => ({
            orderId: item.orderId,
            itemIndex: item.itemIndex,
            quantity: item.quantity
          })) })
      });
      onIssued?.(invoice);
      if (print) window.open(`/admin/facturas/${invoice._id}/print`, '_blank', 'noopener,noreferrer');
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo emitir la factura');
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Facturar mesa ${table?.number || ''}`} size="lg">
      <div style={{ display: 'grid', gap: '0.9rem' }}>
        {error && <div className="service-error" role="alert">{error}</div>}
        {loading ? (
          <p style={{ color: 'var(--a-muted)' }}>Cargando items pendientes...</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['T', 'F'].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSeries(key)}
                  className={series === key ? 'service-station-tab active' : 'service-station-tab'}
                >
                  {key === 'T' ? 'Ticket simplificado' : 'Factura completa'}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[
                ['items', 'Por productos'],
                ['amount', 'Dividir cuenta']
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSplitMode(key)}
                  className={splitMode === key ? 'service-station-tab active' : 'service-station-tab'}
                >
                  {label}
                </button>
              ))}
            </div>

            {splitMode === 'amount' && (
              <div style={{ display: 'grid', gap: '.45rem', padding: '.85rem', border: '1px solid var(--a-border-md)', borderRadius: 10, background: 'var(--a-s1)' }}>
                <label style={{ display: 'grid', gap: '.25rem', color: 'var(--a-muted)', fontSize: '.86rem' }}>
                  Importe a cobrar
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    max={pendingTotals.total}
                    value={splitAmount}
                    onChange={(e) => setSplitAmount(e.target.value)}
                    placeholder={`Máximo ${formatMoney(pendingTotals.total)}`}
                  />
                </label>
                <span style={{ color: 'var(--a-muted)', fontSize: '.82rem' }}>
                  Pendiente de mesa: <strong style={{ color: 'var(--a-text)' }}>{formatMoney(pendingTotals.total)}</strong>. El sistema reparte ese importe contra productos pendientes en orden de comanda.
                </span>
              </div>
            )}

            <div style={{ border: '1px solid var(--a-border-md)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 88px 82px', gap: 8, padding: '0.55rem 0.7rem', color: 'var(--a-muted)', fontSize: '.78rem', textTransform: 'uppercase' }}>
                <span />
                <span>Item</span>
                <span>Cantidad</span>
                <span style={{ textAlign: 'right' }}>Total</span>
              </div>
              {items.length === 0 ? (
                <div style={{ padding: '1rem', color: 'var(--a-muted)' }}>No hay items pendientes de facturar.</div>
              ) : (splitMode === 'amount' ? amountLines : items).map((item) => {
                const key = `${item.orderId}:${item.itemIndex}`;
                const qty = splitMode === 'amount' ? Number(item.quantity || 0) : Number(selected[key] || 0);
                const station = prepStationOf(item);
                return (
                  <div key={key} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 88px 82px', gap: 8, alignItems: 'center', padding: '0.55rem 0.7rem', borderTop: '1px solid var(--a-border)' }}>
                    <input
                      type="checkbox"
                      checked={qty > 0}
                      onChange={(e) => setQty(item, e.target.checked ? item.remainingQty : 0)}
                      aria-label={`Seleccionar ${item.name}`}
                      disabled={splitMode === 'amount'}
                    />
                    <div>
                      <strong>{item.name}</strong>
                      <div style={{ color: 'var(--a-muted)', fontSize: '.82rem' }}>{station === 'barra' ? 'Barra' : 'Cocina'} · {formatMoney(item.priceWithTax)} / ud · pendiente {item.remainingQty}</div>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max={item.remainingQty}
                      step="1"
                      value={qty}
                      onChange={(e) => setQty(item, e.target.value)}
                      style={{ width: '100%' }}
                      readOnly={splitMode === 'amount'}
                    />
                    <strong style={{ textAlign: 'right' }}>{formatMoney(qty * item.priceWithTax)}</strong>
                  </div>
                );
              })}
            </div>

            {series === 'F' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.65rem', padding: '.8rem', border: '1px solid var(--a-border-md)', borderRadius: 10 }}>
                {[
                  ['name', 'Nombre o razón social'],
                  ['nif', 'NIF'],
                  ['address', 'Dirección'],
                  ['email', 'Email']
                ].map(([key, label]) => (
                  <label key={key} style={{ display: 'grid', gap: '.25rem', fontSize: '.84rem', color: 'var(--a-muted)' }}>
                    {label}
                    <input value={customer[key]} onChange={(e) => setCustomer((prev) => ({ ...prev, [key]: e.target.value }))} />
                  </label>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '.55rem' }}>
              {paymentMethods.map((method) => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.key}
                    type="button"
                    onClick={() => setPaymentMethod(method.key)}
                    className={paymentMethod === method.key ? 'service-station-tab active' : 'service-station-tab'}
                    style={{ justifyContent: 'center' }}
                  >
                    <Icon size={15} />
                    {method.label}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'grid', justifyContent: 'end', gap: '.25rem', textAlign: 'right', borderTop: '1px solid var(--a-border-md)', paddingTop: '.8rem' }}>
              <span style={{ color: 'var(--a-muted)' }}>Subtotal {formatMoney(totals.subtotal)} · IVA {formatMoney(totals.taxAmount)}</span>
              <strong style={{ fontSize: '1.8rem', color: 'var(--a-cyan)', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(totals.total)}</strong>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.6rem', flexWrap: 'wrap' }}>
              <Button variant="secondary" onClick={() => setSelected(defaultSelection(items))}>Seleccionar todo</Button>
              <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
                <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                <Button variant="primary" loading={issuing} iconLeft={<Receipt size={16} />} onClick={() => issue(false)}>Emitir solo</Button>
                <Button variant="success" loading={issuing} iconLeft={<Printer size={16} />} onClick={() => issue(true)}>Emitir e imprimir</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
